import {
    Dialog,
    DialogContent,
    DialogActions,
    Typography,
    Box,
    Stack,
    Button,
    TextField,
    CircularProgress,
    Alert,
    MenuItem,
    InputAdornment
  } from "@mui/material";
  import {
    Payment,
    CreditCard,
    ArrowBack,
    CurrencyBitcoin
  } from "@mui/icons-material";
  import { useState } from "react";
  
  interface FiatDepositModalProps {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
    supportedCards?: string[];
    initialAmount?: string;
    labelOverrides?: {
      cardNumber?: string;
      expiry?: string;
      cvc?: string;
      cardHolder?: string;
      amount?: string;
    };
  }
  
  const DEFAULT_CARDS = ["Visa", "MasterCard", "Credit Card", "Debit Card"];
  
  export default function AddCreditCardModal({
    open,
    onClose,
    onSuccess,
    supportedCards = DEFAULT_CARDS,
    initialAmount = "",
    labelOverrides = {}
  }: FiatDepositModalProps) {
    const [cardType, setCardType] = useState("");
    const [cardNumber, setCardNumber] = useState("");
    const [expiry, setExpiry] = useState("");
    const [cardHolder, setCardHolder] = useState("");
    const [cvc, setCvc] = useState("");
    const [amount, setAmount] = useState(initialAmount);
    const [isProcessing, setIsProcessing] = useState(false);
    const [statusMessage, setStatusMessage] = useState<string | null>(null);
    const [statusType, setStatusType] = useState<"success" | "error">("success");
  
    const validate = () => {
      if (
        !cardType ||
        !cardHolder.trim() ||
        !/^\d{16}$/.test(cardNumber) ||
        !/^(0[1-9]|1[0-2])\/[0-9]{2}$/.test(expiry) ||
        !/^\d{3,4}$/.test(cvc) ||
        !/^\d+$/.test(amount)
      ) {
        setStatusMessage("Please enter valid card information.");
        setStatusType("error");
        return false;
      }
      return true;
    };
  
    const formatAmount = (val: string) => {
      const num = val.replace(/[^0-9]/g, "");
      return num.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    };
  
    const handleDeposit = () => {
      if (!validate()) return;
      setIsProcessing(true);
      setStatusMessage(null);
      setTimeout(() => {
        const isSuccess = Math.random() > 0.2;
        setIsProcessing(false);
        setStatusType(isSuccess ? "success" : "error");
        setStatusMessage(
          isSuccess ? "Deposit successful! TaaS Chips have been credited." : "Deposit failed. Please try again."
        );
        if (isSuccess) onSuccess();
      }, 3000);
    };
  
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogContent sx={{ p: 4 }}>
          <Stack spacing={3}>
            <Box display="flex" alignItems="center" gap={2}>
              <CreditCard sx={{ fontSize: 36, color: "#8B5CF6" }} />
              <Box>
                <Typography fontWeight={700} fontSize={20}>Add Fiat Currency</Typography>
                <Typography fontSize={14} color="text.secondary">
                  Use your card to deposit fiat and purchase TaaS Chips.
                </Typography>
              </Box>
            </Box>
  
            <TextField
              select
              fullWidth
              label="Card Type"
              value={cardType}
              onChange={(e) => setCardType(e.target.value)}
            >
              {supportedCards.map((type) => (
                <MenuItem key={type} value={type}>{type}</MenuItem>
              ))}
            </TextField>
  
            <TextField
              fullWidth
              label={labelOverrides.cardNumber || "Card Number"}
              placeholder="1234 5678 9012 3456"
              value={cardNumber}
              onChange={(e) => setCardNumber(e.target.value.replace(/[^0-9]/g, "").slice(0, 16))}
            />
  
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                fullWidth
                label={labelOverrides.expiry || "Expiry Date (MM/YY)"}
                placeholder="08/26"
                value={expiry}
                onChange={(e) => setExpiry(e.target.value)}
              />
              <TextField
                fullWidth
                label={labelOverrides.cvc || "CVC"}
                placeholder="123"
                value={cvc}
                onChange={(e) => setCvc(e.target.value.replace(/[^0-9]/g, "").slice(0, 4))}
              />
            </Stack>
  
            <TextField
              fullWidth
              label={labelOverrides.cardHolder || "Card Holder Name"}
              placeholder="Enter full name"
              value={cardHolder}
              onChange={(e) => setCardHolder(e.target.value)}
            />
  
            <TextField
              fullWidth
              label={labelOverrides.amount || "Amount to Deposit"}
              placeholder="$1,000"
              value={formatAmount(amount)}
              onChange={(e) => setAmount(e.target.value.replace(/[^0-9]/g, ""))}
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>
              }}
            />
  
            {isProcessing && (
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={20} />
                <Typography fontSize={14}>Processing your deposit...</Typography>
              </Stack>
            )}
  
            {statusMessage && (
              <Alert severity={statusType}>{statusMessage}</Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 4, pb: 3 }}>
          <Button onClick={onClose} variant="outlined" sx={{ borderRadius: 2 }}>
            Cancel
          </Button>
          <Button
            onClick={handleDeposit}
            variant="contained"
            startIcon={<CurrencyBitcoin />}
            disabled={isProcessing}
            sx={{ borderRadius: 2, backgroundColor: "#8B5CF6", textTransform: "none" }}
          >
            Deposit
          </Button>
        </DialogActions>
      </Dialog>
    );
  }